'use client';

import { c as _c } from "react/compiler-runtime";
import './volunteer-slot-card.scss';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Prominence, Purposes, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '@sola/ark-core/molecules/tooltip/tooltip.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { GraphQLVolunteerStatus } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { type SlotCardContactInput, getVolunteerSlotCardData } from './volunteer-slot-card.logic.ts';
interface ConfirmTooltipProps extends PropsWithChildren {
  disabled: boolean;
  status?: GraphQLVolunteerStatus | null;
}
export type VolunteerSlotCardProps = {
  startTime: Date | string;
  endTime: Date | string;
  title: string;
  status?: GraphQLVolunteerStatus | null;
  contacts: SlotCardContactInput[];
  className?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
};
const ConfirmTooltip: React.FC<ConfirmTooltipProps> = t0 => {
  const $ = _c(10);
  const {
    children,
    disabled,
    status
  } = t0;
  const {
    t
  } = useTranslation("volunteers");
  if (disabled) {
    return children;
  }
  let t1;
  if ($[0] !== status || $[1] !== t) {
    t1 = status === GraphQLVolunteerStatus.Approved ? t("volunteers:confirmDisabledTooltip.outsideWindow") : t("volunteers:confirmDisabledTooltip.pendingApproval");
    $[0] = status;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const message = t1;
  let t2;
  if ($[3] !== children) {
    t2 = <TooltipTrigger asChild={true}><span className="inline-flex">{children}</span></TooltipTrigger>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== message) {
    t3 = <TooltipContent align="start" side="top" size="sm"><p>{message}</p></TooltipContent>;
    $[5] = message;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <Tooltip>{t2}{t3}</Tooltip>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};
interface VolunteerSlotCardShiftDetailProps extends PropsWithChildren {
  iconName: IconNames;
  label: string;
}
const VolunteerSlotCardShiftDetail: React.FC<VolunteerSlotCardShiftDetailProps> = t0 => {
  const $ = _c(8);
  const {
    iconName,
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== iconName || $[1] !== label) {
    t1 = <dt className="flex"><ArkIcon iconName={iconName} size="20" alt={label} /></dt>;
    $[0] = iconName;
    $[1] = label;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== children) {
    t2 = <dd className="text-wrap">{children}</dd>;
    $[3] = children;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className="flex items-center gap-snug-xl">{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export const VolunteerSlotCard: React.FC<VolunteerSlotCardProps> = t0 => {
  const $ = _c(58);
  const {
    startTime,
    endTime,
    title,
    status,
    contacts,
    className,
    onCancel,
    onConfirm
  } = t0;
  const {
    i18n,
    t
  } = useTranslation("volunteers");
  let T0;
  let isCancelHidden;
  let isConfirmDisabled;
  let isConfirmHidden;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== contacts || $[2] !== endTime || $[3] !== i18n.language || $[4] !== i18n.resolvedLanguage || $[5] !== startTime || $[6] !== status || $[7] !== t || $[8] !== title) {
    const {
      badgeColor,
      contactList,
      dateTime,
      formattedDate,
      isCancelHidden: t11,
      isConfirmDisabled: t12,
      isConfirmHidden: t13,
      timeRange
    } = getVolunteerSlotCardData({
      contacts,
      endTime,
      language: i18n.resolvedLanguage ?? i18n.language,
      startTime,
      status
    });
    isCancelHidden = t11;
    isConfirmDisabled = t12;
    isConfirmHidden = t13;
    if ($[23] !== className) {
      t9 = cn("VolunteerSlotCard grid max-w-sm rounded-md p-comfy-lg gap-comfy-md", className);
      $[23] = className;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t14;
    if ($[25] !== title) {
      t14 = <Text asChild={true} variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent}><h3 className="label-neutral-mild m-none">{title}</h3></Text>;
      $[25] = title;
      $[26] = t14;
    } else {
      t14 = $[26];
    }
    t10 = <div className="flex items-start justify-between gap-comfy-sm"><div className="grid min-w-0 gap-snug-md">{timeRange && <Text asChild={true} variant={Variants.Heading} size={Sizes.XSmall} prominence={Prominence.Amplified}><time className="VolunteerSlotCard__heading" dateTime={dateTime}>{timeRange}</time></Text>}{t14}</div>{badgeColor && status && <Badge color={badgeColor} label={t(`volunteers:statuses.${status}`)} size="sm" />}</div>;
    t7 = "grid gap-snug-xl";
    let t15;
    if ($[27] !== t) {
      t15 = t("volunteers:slotCard.shiftDetails.title");
      $[27] = t;
      $[28] = t15;
    } else {
      t15 = $[28];
    }
    if ($[29] !== t15) {
      t8 = <Text asChild={true} variant={Variants.Label} size={Sizes.Large} purpose={Purposes.Neutral}><h4 className="mb-snug-md">{t15}</h4></Text>;
      $[29] = t15;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    T0 = Text;
    t4 = Variants.Label;
    t5 = Prominence.Subtle;
    t6 = true;
    t1 = "VolunteerSlotCard__shift-details grid label-neutral-mild gap-snug-lg";
    t2 = startTime && <VolunteerSlotCardShiftDetail iconName="calendar" label={t("volunteers:slotCard.shiftDetails.dateLabel")}>{formattedDate}</VolunteerSlotCardShiftDetail>;
    t3 = contactList.length > 0 && <VolunteerSlotCardShiftDetail iconName="volunteer" label={t("volunteers:slotCard.shiftDetails.contactsLabel")}><span className="flex flex-wrap">{contactList.map(_temp)}</span></VolunteerSlotCardShiftDetail>;
    $[0] = className;
    $[1] = contacts;
    $[2] = endTime;
    $[3] = i18n.language;
    $[4] = i18n.resolvedLanguage;
    $[5] = startTime;
    $[6] = status;
    $[7] = t;
    $[8] = title;
    $[9] = T0;
    $[10] = isCancelHidden;
    $[11] = isConfirmDisabled;
    $[12] = isConfirmHidden;
    $[13] = t1;
    $[14] = t10;
    $[15] = t2;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
    $[21] = t8;
    $[22] = t9;
  } else {
    T0 = $[9];
    isCancelHidden = $[10];
    isConfirmDisabled = $[11];
    isConfirmHidden = $[12];
    t1 = $[13];
    t10 = $[14];
    t2 = $[15];
    t3 = $[16];
    t4 = $[17];
    t5 = $[18];
    t6 = $[19];
    t7 = $[20];
    t8 = $[21];
    t9 = $[22];
  }
  let t11;
  if ($[31] !== t1 || $[32] !== t2 || $[33] !== t3) {
    t11 = <dl className={t1}>{t2}{t3}</dl>;
    $[31] = t1;
    $[32] = t2;
    $[33] = t3;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== T0 || $[36] !== t11 || $[37] !== t4 || $[38] !== t5 || $[39] !== t6) {
    t12 = <T0 variant={t4} prominence={t5} asChild={t6}>{t11}</T0>;
    $[35] = T0;
    $[36] = t11;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== t12 || $[42] !== t7 || $[43] !== t8) {
    t13 = <div className={t7}>{t8}{t12}</div>;
    $[41] = t12;
    $[42] = t7;
    $[43] = t8;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== isCancelHidden || $[46] !== isConfirmDisabled || $[47] !== isConfirmHidden || $[48] !== onCancel || $[49] !== onConfirm || $[50] !== status || $[51] !== t) {
    t14 = (!isConfirmHidden || !isCancelHidden) && (onConfirm || onCancel) && <div className="flex items-center mt-snug-md gap-comfy-sm">{!isConfirmHidden && onConfirm && <ConfirmTooltip disabled={!isConfirmDisabled} status={status}><ButtonStyle disabled={isConfirmDisabled} size="md"><button type="button" onClick={onConfirm}>{t("volunteers:slotCard.actions.confirm")}</button></ButtonStyle></ConfirmTooltip>}{!isCancelHidden && onCancel && <LinkStyle noUnderline={true} purpose="negative" size="md" noPadding={true}><button type="button" onClick={onCancel}>{t("volunteers:slotCard.actions.cancel")}</button></LinkStyle>}</div>;
    $[45] = isCancelHidden;
    $[46] = isConfirmDisabled;
    $[47] = isConfirmHidden;
    $[48] = onCancel;
    $[49] = onConfirm;
    $[50] = status;
    $[51] = t;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  let t15;
  if ($[53] !== t10 || $[54] !== t13 || $[55] !== t14 || $[56] !== t9) {
    t15 = <div className={t9}>{t10}{t13}{t14}</div>;
    $[53] = t10;
    $[54] = t13;
    $[55] = t14;
    $[56] = t9;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  return t15;
};
export const VolunteerSlotCardSkeleton: React.FC<{
  className?: string;
}> = t0 => {
  const $ = _c(10);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("VolunteerSlotCard grid max-w-sm rounded-md p-comfy-lg gap-comfy-md", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-start justify-between gap-comfy-sm"><div className="grid min-w-0 flex-1 gap-snug-lg"><Skeleton width="8rem" height={24} /><Skeleton width="12rem" height={20} /></div><Skeleton width="6rem" height={30} borderRadius="var(--outline-radius-lg)" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="mb-snug-md" width="8rem" height={20} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex items-center gap-snug-xl"><Skeleton width={24} height={20} /><Skeleton width="13rem" height={20} /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center gap-snug-xl"><Skeleton width={24} height={20} /><Skeleton width="10rem" height={20} /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="grid gap-snug-xl">{t3}<div className="grid gap-snug-lg">{t4}{t5}<div className="flex items-center gap-snug-xl"><Skeleton width={24} height={20} /><Skeleton width="12rem" height={20} /></div></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center mt-snug-md gap-snug-lg"><Skeleton width={86} height={42} borderRadius={8} /><Skeleton width={78} height={42} borderRadius={8} /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t1) {
    t8 = <div className={t1}>{t2}{t6}{t7}</div>;
    $[8] = t1;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  return t8;
};
function _temp(contact, idx) {
  return <span key={contact.name} className="inline-flex items-center gap-snug-md">{idx > 0 && <span aria-hidden="true">,</span>}<span>{contact.name}</span></span>;
}